// src/modules/app-views/companies/pages/Document/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { SimpleFormProvider } from 'components/SimpleComponents/SimpleFormProvider';
import useZustand from 'hooks/useZustand';
import { Tabs } from 'antd';
import translateX from 'utils/translateX';
import { useEffect, useMemo, useState } from 'react';
import { useDocumentContext } from 'components/core/DocumentProvider';
import appService from 'services/appService';
import SimpleFormProviderInitializer from 'components/SimpleComponents/SimpleFormProviderInitializer';
import SimpleLoadingArea from 'components/SimpleComponents/SimpleLoadingArea';
import { buildInitialValues } from 'utils/mock';
import useFormInstance from 'hooks/useFormInstance';
import OrganizationRecordTab from 'modules/app-views/account/pages/Subscription/tabs/RecordTab';
import CardHeaderWithCountry from 'layouts/Pages/PageHeader/CardHeaderWithCountry';
import authService from 'services/authService';
import fiscalService from 'services/fiscalService';
import useFiscalDataTracking from 'hooks/useFiscalDataTracking';
import { useSearchParams } from 'react-router-dom';
import { parseImportToken, QUERY_PARAM_NAME } from 'utils/crossProfileImport';
import { buildOriginIcon } from 'components/JarvislyComponents/DocumentInfoIcons';
import EntityRecordTab from 'modules/app-views/companies/pages/Document/tabs/RecordTab';
import EntityAdditionalInfoTab from 'modules/app-views/companies/pages/Document/tabs/AdditionalInfoTab';
import EnrichmentTab from 'modules/app-views/companies/pages/Document/tabs/EnrichmentTab';
import { FileSyncOutlined, InfoCircleOutlined } from '@ant-design/icons';
import DocumentIdentifier from 'components/core/DocumentIdentifier';
import BackToHomeLink from 'components/core/BackToHomeLink';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const isMockStep = false;

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const CompaniesDocument = props => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const storedSubscription = useZustand('auth', 'subscription');
  const { tab, initTab, handleTabChange, availableTabs, buildTabs, data, viewMode, registerTabFieldPrefixes } =
    useDocumentContext();

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  // Get upsert from global state - auto-fetch is handled by DocumentProvider
  const { upsert, urlInfo } = useZustand('global', 'useApi');
  const { formRef, form, isInitializedRef, excludeFieldsFromReset } = useFormInstance({ data });
  const _id = urlInfo?.params?._id;
  const [searchParams, setSearchParams] = useSearchParams();

  // Check for cross-profile import token
  const importToken = searchParams.get(QUERY_PARAM_NAME);
  const importData = useMemo(() => parseImportToken(importToken), [importToken]);

  // Centralized fiscal data tracking hook
  const { getSavedFiscalData, updateSavedFiscalData } = useFiscalDataTracking(data);

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const locale = appService.getLocale();
  const requiredFields = [
    'countryCode',

    'cnpj.value',
    'name',
    'fullName',
    'phone.value',
    'email.value',
    'foundAt',

    'address.zip',
    'address.number',
    'address.countryCode',
  ];

  if (locale !== 'BR') {
    requiredFields.push('taxId', 'address.address1', 'address.region', 'address.city');
  }

  const tabsItems = useMemo(() => {
    const src = Array.isArray(availableTabs)
      ? availableTabs
      : availableTabs
        ? Object.values(availableTabs)
        : [];
    return src;
  }, [availableTabs]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  // NOTE: useMemo instead of useState to react when data becomes available
  const initialValues = useMemo(() => buildInitialValues(isMockStep, data), [data]);
  const [disabledFields, setDisabledFields] = useState([]);
  const [state, setState] = useState({
    dataOrigin: 'new_data',
    entityType: 'company',
    entityProfile: ['subscription', 'company'],
  });

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!initTab) return;
    registerTabFieldPrefixes?.({ '': initTab });
  }, [registerTabFieldPrefixes, initTab]);

  useEffect(() => {
    const childProps = {
      ...props,
      data,
      initTab,
      state,
      setState,
      viewMode,
    };
    const itemsLocal = buildTabsArray(childProps);
    buildTabs(itemsLocal);
  }, [state, initTab, viewMode]); // keep behavior

  // NOTE: savedFiscalDataRef initialization is now handled by useFiscalDataTracking hook

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleFinish = async ({ values, allValues }) => {
    try {
      // Get _id from multiple sources (fallback pattern like SubscriptionsDocument)
      const entityId = values?._id || allValues?._id || data?._id;

      // NOTE: Fiscal service validation is now done in each tab's toggle handler (NfceTab, NfeTab, NfseTab)
      // in the fiscal-config module. The validateFiscalServicesBeforeSave call was removed.

      // Ensure fiscalData is included in the save operation
      // fiscalData fields come from the FiscalTab form inputs
      // Normalize fiscalData to convert cleared fields (allowClear) to explicit null
      const normalizedFiscalData = allValues?.fiscalData
        ? fiscalService.normalizeFiscalData(allValues.fiscalData, getSavedFiscalData())
        : undefined;

      // Start with form values
      const dataToSave = {
        ...values,
        ...(normalizedFiscalData && { fiscalData: normalizedFiscalData }),
      };

      // For new documents (manual registration), explicitly set origin to 0 (manual)
      // This prevents backend from defaulting to origin: 2 (automatic)
      const isNewDocument = !_id || _id === 'add';
      if (isNewDocument) {
        dataToSave._metadata = { ...dataToSave._metadata, origin: 0 };
      }

      // CROSS-PROFILE IMPORT: Add target profile to dataProfile array
      // This ensures the entity now belongs to both the original and target profiles
      if (importData && importData.targetProfile?.length > 0) {
        const currentDataProfile = data?.dataProfile || [];
        const targetProfile = importData.targetProfile;

        // Merge profiles without duplicates
        const mergedProfiles = [...new Set([...currentDataProfile, ...targetProfile])];

        // Only update if there are new profiles to add
        if (mergedProfiles.length > currentDataProfile.length) {
          dataToSave.dataProfile = mergedProfiles;
        }
      }

      // Include contacts array from data to prevent losing contacts added via EntityChannelsCard
      // But sync form field changes (phone/email) with the corresponding contact entries
      if (data?.contacts) {
        let mergedContacts = [...data.contacts];

        // Sync phone changes from form to contacts array
        const phoneValue = allValues?.phone?.value || values?.phone?.value;
        const phoneId = allValues?.phone?._id || values?.phone?._id || data?.phone?._id;
        if (phoneValue && phoneId) {
          mergedContacts = mergedContacts.map(c =>
            c._id === phoneId ? { ...c, value: phoneValue } : c
          );
        }

        // Sync email changes from form to contacts array
        const emailValue = allValues?.email?.value || values?.email?.value;
        const emailId = allValues?.email?._id || values?.email?._id || data?.email?._id;
        if (emailValue && emailId) {
          mergedContacts = mergedContacts.map(c =>
            c._id === emailId ? { ...c, value: emailValue } : c
          );
        }

        dataToSave.contacts = mergedContacts;
      }

      // Show "saving" message only AFTER fiscal validations pass (if any)
      // This provides better UX: user sees "Validating..." then "Saving..."
      await appService.message({ flag: 'i', text: 'saving_data', key: 'saveContext' });

      await upsert({ dataSet: dataToSave, _id: entityId });

      // Handle fiscal field clearing - capture BEFORE signInRefresh to prevent flash
      if (allValues?.fiscalData) {
        const originalFiscalData = getSavedFiscalData();
        const clearedFields = fiscalService.getClearedFiscalFields(
          allValues.fiscalData,
          originalFiscalData
        );

        if (clearedFields.length > 0) {
          // Mark these fields to be excluded from the next auto-reset in useFormInstance
          excludeFieldsFromReset(clearedFields);
        }

        // Update the tracking ref
        updateSavedFiscalData(allValues.fiscalData);
      }

      // refresh account subscription data if user is editing an main company linked with a subscription
      if (storedSubscription?.entityId === entityId) await authService.signInRefresh();

      // Clear the form fields after signInRefresh (the excluded fields won't flash back)
      if (allValues?.fiscalData) {
        fiscalService.clearFiscalFieldsInForm(form, allValues.fiscalData, getSavedFiscalData());
      }

      // CROSS-PROFILE IMPORT: Remove import token from URL after successful save
      // This cleans up the URL and prevents re-processing on page refresh
      if (importToken) {
        searchParams.delete(QUERY_PARAM_NAME);
        setSearchParams(searchParams, { replace: true });
      }
    } catch (err) {
      throw err;
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <>
      <div className="container mt-3">
        <SimpleFormProvider
          formRef={formRef}
          onFinish={handleFinish}
          showSubmitButton={false}
          initFocus={locale === 'BR' ? 'cnpj.value' : 'name'}
          additionalValues={{
            entityProfile: 'company',
          }}
          reloadEntirePage={true}
          ignoreResetFormOnSubmit={true}
          requiredFields={requiredFields}
          disabledFields={disabledFields}
          locked={viewMode === 'view'}
          showInfoMessage={false}
          // forceViewAfterSave={false}
          initialValues={initialValues}
          data={data}
        >
          <SimpleFormProviderInitializer data={data} isInitializedRef={isInitializedRef} />

          <div className="position-relative">
            <CardHeaderWithCountry
              overlapsBackground
              data={data}
              avatarName={data?.organization}
              watchField="countryCode"
            />

            <SimpleLoadingArea
              name="cardHeader"
              offsetTop={0}
              showProgress={false}
              zIndex={100}
              borderRadius={8}
            />
          </div>

          <Tabs
            type="line"
            tabPlacement="top"
            activeKey={tab}
            rootClassName="tab-in-form no-content-padding"
            items={tabsItems}
            onChange={handleTabChange}
            animated={{ inkBar: true, tabPane: false }}
            destroyOnHidden={false}
          />

          {/*<DocumentIdentifier data={data} />*/}
        </SimpleFormProvider>
      </div>
    </>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default CompaniesDocument;

// *******************************************************************************
// HELPERS
// *******************************************************************************
function buildTabsArray(props) {
  const { initTab, data } = props;

  return [
    {
      key: initTab,
      icon: buildOriginIcon(data),
      label: <span>{translateX('record')}</span>,
      hide: false,
      disabled: false,
      children: <EntityRecordTab {...props} tabName={initTab} />,
    },
    {
      key: 'enrichment',
      icon: <FileSyncOutlined />,
      label: <span>{translateX('additional_info')}</span>,
      hide: false,
      disabled: false,
      children: <EnrichmentTab />,
    },
  ];
}

// Fiscal validation helpers moved to fiscalService.js
// Use: fiscalService.hasNfceFieldsChanged, fiscalService.hasNfeFieldsChanged, fiscalService.hasNfseFieldsChanged
